import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  Calendar,
  CircleAlert,
  FileText,
  Image as ImageIcon,
  Inbox,
  Loader2,
  Megaphone,
  MessageSquare,
  Plus,
  Quote,
  Settings,
  Users,
} from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import { useAdminAuth } from "@/hooks/useAdminAuth";

interface Stats {
  submissionsTotal: number;
  submissionsNew: number;
  announcementsActive: number;
  eventsUpcoming: number;
  propertiesPublished: number;
  propertiesDraft: number;
  testimonialsPublished: number;
  teamPublished: number;
  mediaAssets: number;
}

interface RecentRow {
  id: string;
  name: string;
  email: string;
  interest: string;
  status: string;
  created_at: string;
}

const StatCard = ({
  label,
  value,
  icon: Icon,
  to,
}: {
  label: string;
  value: number | string;
  icon: React.ComponentType<{ className?: string }>;
  to?: string;
}) => {
  const inner = (
    <>
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <p className="mt-3 font-headline text-3xl text-foreground">{value}</p>
    </>
  );
  return to ? (
    <Link to={to} className="block border border-border bg-card p-5 transition-colors hover:border-accent/40">
      {inner}
    </Link>
  ) : (
    <div className="border border-border bg-card p-5">{inner}</div>
  );
};

const QuickAction = ({
  to,
  label,
  icon: Icon,
}: {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}) => (
  <Link
    to={to}
    className="flex items-center gap-3 border border-border bg-card px-4 py-3 text-sm text-foreground transition-colors hover:border-accent/40 hover:bg-muted/40"
  >
    <Icon className="h-4 w-4 text-accent" />
    <span className="flex-1">{label}</span>
    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
  </Link>
);

const todayIso = () => new Date().toISOString().slice(0, 10);

const AdminDashboard = () => {
  const { user } = useAdminAuth();
  const [stats, setStats] = useState<Stats | null>(null);
  const [recent, setRecent] = useState<RecentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Dashboard | Hadassah Homes Admin";
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const nowIso = new Date().toISOString();
        const today = todayIso();
        const [
          subTotal,
          subNew,
          annActive,
          evUpcoming,
          propPub,
          propDraft,
          testPub,
          teamPub,
          media,
          recentR,
        ] = await Promise.all([
          supabase.from("contact_submissions").select("id", { count: "exact", head: true }),
          supabase.from("contact_submissions").select("id", { count: "exact", head: true }).eq("status", "new"),
          supabase.from("announcements").select("id", { count: "exact", head: true }).eq("is_active", true),
          supabase
            .from("events")
            .select("id", { count: "exact", head: true })
            .eq("is_published", true)
            .gte("event_date", today),
          supabase.from("properties").select("id", { count: "exact", head: true }).eq("is_published", true),
          supabase.from("properties").select("id", { count: "exact", head: true }).eq("is_published", false),
          supabase.from("testimonials").select("id", { count: "exact", head: true }).eq("is_published", true),
          supabase.from("team_members").select("id", { count: "exact", head: true }).eq("is_published", true),
          supabase.from("media_assets").select("id", { count: "exact", head: true }).eq("is_archived", false),
          supabase
            .from("contact_submissions")
            .select("id,name,email,interest,status,created_at")
            .order("created_at", { ascending: false })
            .limit(5),
        ]);

        // Suppress unused warning for nowIso (kept for potential future filters)
        void nowIso;

        if (!mounted) return;
        setStats({
          submissionsTotal: subTotal.count ?? 0,
          submissionsNew: subNew.count ?? 0,
          announcementsActive: annActive.count ?? 0,
          eventsUpcoming: evUpcoming.count ?? 0,
          propertiesPublished: propPub.count ?? 0,
          propertiesDraft: propDraft.count ?? 0,
          testimonialsPublished: testPub.count ?? 0,
          teamPublished: teamPub.count ?? 0,
          mediaAssets: media.count ?? 0,
        });
        setRecent((recentR.data ?? []) as RecentRow[]);
      } catch (e) {
        console.error(e);
        if (mounted) setError("Could not load dashboard data.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <AdminLayout
      title="Dashboard"
      description="Snapshot of activity across the public website. All counts are live from the database."
      publicHref="/"
      publicLabel="View website"
    >
      <div className="mx-auto max-w-6xl space-y-8">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-accent">Welcome back</p>
          <h2 className="mt-1 font-headline text-3xl text-foreground md:text-4xl">
            {user?.email ? `Hello, ${user.email.split("@")[0]}` : "Hello"}
          </h2>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="border border-destructive/30 bg-destructive/5 p-6 text-destructive">{error}</div>
        ) : (
          <>
            {/* Counts */}
            <section className="space-y-3">
              <h3 className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">At a glance</h3>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                <StatCard label="New submissions" value={stats?.submissionsNew ?? 0} icon={CircleAlert} to="/admin/submissions" />
                <StatCard label="Total submissions" value={stats?.submissionsTotal ?? 0} icon={Inbox} to="/admin/submissions" />
                <StatCard label="Active announcements" value={stats?.announcementsActive ?? 0} icon={Megaphone} to="/admin/announcements" />
                <StatCard label="Upcoming events" value={stats?.eventsUpcoming ?? 0} icon={Calendar} to="/admin/events" />
                <StatCard label="Published properties" value={stats?.propertiesPublished ?? 0} icon={Building2} to="/admin/properties" />
                <StatCard label="Draft properties" value={stats?.propertiesDraft ?? 0} icon={Building2} to="/admin/properties" />
                <StatCard label="Published testimonials" value={stats?.testimonialsPublished ?? 0} icon={Quote} to="/admin/testimonials" />
                <StatCard label="Published team" value={stats?.teamPublished ?? 0} icon={Users} to="/admin/team" />
                <StatCard label="Media assets" value={stats?.mediaAssets ?? 0} icon={ImageIcon} to="/admin/media" />
              </div>
            </section>

            {/* Quick actions */}
            <section className="space-y-3">
              <h3 className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Quick actions</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <QuickAction to="/admin/submissions" label="View submissions" icon={Inbox} />
                <QuickAction to="/admin/pages" label="Edit page content" icon={FileText} />
                <QuickAction to="/admin/announcements" label="Add announcement" icon={Megaphone} />
                <QuickAction to="/admin/events" label="Add event" icon={Calendar} />
                <QuickAction to="/admin/properties" label="Add property" icon={Plus} />
                <QuickAction to="/admin/media" label="Upload media" icon={ImageIcon} />
                <QuickAction to="/admin/settings" label="Edit site settings" icon={Settings} />
              </div>
            </section>

            {/* Latest submissions */}
            <section className="border border-border bg-card">
              <div className="flex items-center justify-between border-b border-border px-5 py-4">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  <h3 className="font-headline text-lg text-foreground">Latest submissions</h3>
                </div>
                <Link to="/admin/submissions" className="inline-flex items-center gap-1 text-sm text-accent hover:text-accent/80">
                  View all <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
              {recent.length === 0 ? (
                <p className="px-5 py-10 text-center text-sm text-muted-foreground">No submissions yet.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {recent.map((r) => (
                    <li
                      key={r.id}
                      className="flex flex-col gap-1 px-5 py-4 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-medium text-foreground">{r.name}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          {r.email} · {r.interest}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="uppercase tracking-[0.12em]">{r.status}</span>
                        <span>{new Date(r.created_at).toLocaleDateString()}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
